import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connectApi } from "../lib/api";
import { toast } from "sonner";
import { Radio, Plus, Play, Square, Trash2, Users, Video, Loader2, Circle, Link2, Film, Save, Eye, X, CalendarClock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

const ESTADO = {
  programada: { label: "Programada", cls: "bg-slate-100 text-slate-600" },
  en_vivo: { label: "EN VIVO", cls: "bg-[#C0140F] text-white" },
  finalizada: { label: "Finalizada", cls: "bg-slate-100 text-slate-400" },
};

export default function ConnectManager() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("sesiones");
  const [sessions, setSessions] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titulo: "", descripcion: "", scheduled_at: "", programas: "", materia_id: "", host_instructor_id: "", zoom_meeting_number: "", zoom_passcode: "" });
  const [saving, setSaving] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [linkFor, setLinkFor] = useState(null);
  const [player, setPlayer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, list, recs, cls] = await Promise.all([
        connectApi.config(), connectApi.list(), connectApi.recordings().catch(() => []), connectApi.campusClasses().catch(() => []),
      ]);
      setConfigured(cfg.configured);
      setSessions(list); setRecordings(recs); setClasses(cls);
      connectApi.instructors().then(setInstructors).catch(() => {});
    } catch (e) { toast.error("No se pudo cargar."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.titulo.trim()) { toast.error("Escribe un título."); return; }
    setSaving(true);
    try {
      const programas = form.programas.split(",").map((x) => x.trim()).filter(Boolean);
      const s = await connectApi.create({
        titulo: form.titulo, descripcion: form.descripcion,
        scheduled_at: form.scheduled_at || "",
        host_instructor_id: form.host_instructor_id || "",
        zoom_meeting_number: form.zoom_meeting_number || "",
        zoom_passcode: form.zoom_passcode || "",
        audiencia: programas.length ? "programas" : "todos", programas,
      });
      if (form.materia_id) { try { await connectApi.linkClass(s.id, form.materia_id); } catch {} }
      setOpen(false); setForm({ titulo: "", descripcion: "", scheduled_at: "", programas: "", materia_id: "", host_instructor_id: "", zoom_meeting_number: "", zoom_passcode: "" });
      toast.success("Sesión creada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const start = async (s) => { try { await connectApi.start(s.id); navigate(`/connect/room/${s.id}`); } catch { toast.error("No se pudo iniciar."); } };
  const enter = (s) => navigate(`/connect/room/${s.id}`);
  const end = async (s) => { try { await connectApi.end(s.id); toast.success("Finalizada"); load(); } catch { toast.error("Error"); } };
  const remove = async (s) => { if (!window.confirm(`¿Eliminar "${s.titulo}"?`)) return; try { await connectApi.remove(s.id); toast.success("Eliminada"); load(); } catch { toast.error("Error"); } };

  const playRec = async (r) => {
    setPlayer({ titulo: r.titulo, url: null });
    try { const blob = await connectApi.recStreamBlob(r.id); setPlayer({ titulo: r.titulo, url: URL.createObjectURL(blob) }); }
    catch { setPlayer(null); toast.error("No se pudo cargar la grabación."); }
  };

  return (
    <div data-testid="connect-manager">
      {!configured && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">Supera Connect aún no está configurado (faltan las credenciales de Zoom).</div>
      )}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#C0140F] flex items-center justify-center"><Radio className="w-6 h-6 text-white" /></div>
          <div><h1 className="text-2xl font-extrabold text-[#0A1C30] leading-tight">Supera Connect</h1><p className="text-sm text-[#6B7688]">Transmite tus clases y webinars en vivo.</p></div>
        </div>
        {tab === "sesiones" && <Button onClick={() => setOpen(true)} className="bg-[#C0140F] hover:bg-[#a01109]" data-testid="connect-new-btn"><Plus className="w-4 h-4 mr-2" /> Nueva sesión</Button>}
      </div>

      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("sesiones")} className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === "sesiones" ? "bg-[#0A1C30] text-white" : "bg-white text-[#6B7688] ring-1 ring-black/5"}`} data-testid="connect-tab-sesiones">Sesiones</button>
        <button onClick={() => setTab("grabaciones")} className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-1.5 ${tab === "grabaciones" ? "bg-[#0A1C30] text-white" : "bg-white text-[#6B7688] ring-1 ring-black/5"}`} data-testid="connect-tab-grabaciones"><Film className="w-4 h-4" /> Grabaciones ({recordings.length})</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#6B7688]" /></div>
      ) : tab === "sesiones" ? (
        sessions.length === 0 ? (
          <div className="text-center py-16 text-[#6B7688]" data-testid="connect-empty"><Radio className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-bold">Aún no tienes sesiones.</p><p className="text-sm">Crea tu primera clase o webinar en vivo.</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="connect-list">
            {sessions.map((s) => {
              const e = ESTADO[s.estado] || ESTADO.programada;
              const linked = classes.find((c) => c.connect_session_id === s.id);
              return (
                <div key={s.id} className="rounded-2xl border p-4 bg-white border-black/5" data-testid={`connect-session-${s.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`text-[10px] font-extrabold px-2 py-1 rounded-md tracking-wider flex items-center gap-1 ${e.cls}`}>{s.estado === "en_vivo" && <Circle className="w-2 h-2 fill-current animate-pulse" />}{e.label}</span>
                    {s.estado === "en_vivo" && <span className="text-xs flex items-center gap-1 text-[#6B7688]"><Users className="w-3.5 h-3.5" /> {s.online_count || 0}</span>}
                  </div>
                  <h3 className="font-extrabold text-[#0A1C30] line-clamp-1">{s.titulo}</h3>
                  {s.descripcion && <p className="text-sm mt-0.5 line-clamp-2 text-[#6B7688]">{s.descripcion}</p>}
                  {s.scheduled_at && <p className="text-[11px] mt-1 text-[#6B7688] flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {new Date(s.scheduled_at).toLocaleString("es")}</p>}
                  <p className="text-[11px] mt-1 text-[#6B7688]">Host: {s.host_nombre}{linked ? ` · 🔗 ${linked.titulo}` : ""}</p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {s.estado === "en_vivo" ? (
                      <>
                        <Button size="sm" onClick={() => enter(s)} className="bg-[#C0140F] hover:bg-[#a01109]" data-testid={`connect-enter-${s.id}`}><Video className="w-4 h-4 mr-1" /> Entrar</Button>
                        <Button size="sm" variant="outline" onClick={() => end(s)} data-testid={`connect-end-${s.id}`}><Square className="w-4 h-4" /></Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => start(s)} className="bg-[#22A860] hover:bg-[#1c8f50]" data-testid={`connect-start-${s.id}`}><Play className="w-4 h-4 mr-1" /> {s.estado === "finalizada" ? "Reiniciar" : "Iniciar"}</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setLinkFor(s)} data-testid={`connect-link-${s.id}`} title="Vincular a clase"><Link2 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(s)} data-testid={`connect-delete-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <RecordingsPanel recordings={recordings} onChanged={load} onPlay={playRec} />
      )}

      {/* Nueva sesión */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="connect-new-dialog">
          <DialogHeader><DialogTitle>Nueva sesión en vivo</DialogTitle><DialogDescription>Programa una clase o webinar. Podrás iniciarla cuando quieras.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título de la sesión" value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} data-testid="connect-titulo" />
            <Textarea placeholder="Descripción (opcional)" value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} data-testid="connect-desc" />
            <div className="rounded-xl bg-[#F4F6F9] p-3 space-y-2">
              <label className="text-xs font-bold text-[#0A1C30]">Reunión de Zoom (el anfitrión la inicia desde su cuenta Zoom)</label>
              <Input placeholder="ID de reunión de Zoom (ej. 123 4567 8901)" value={form.zoom_meeting_number} onChange={(e) => setForm((f) => ({ ...f, zoom_meeting_number: e.target.value }))} data-testid="connect-zoom-id" />
              <Input placeholder="Contraseña de la reunión (opcional)" value={form.zoom_passcode} onChange={(e) => setForm((f) => ({ ...f, zoom_passcode: e.target.value }))} data-testid="connect-zoom-pass" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#6B7688]">Instructor anfitrión (aparecerá en su panel "Conectarme a mi Clase")</label>
              <select value={form.host_instructor_id} onChange={(e) => setForm((f) => ({ ...f, host_instructor_id: e.target.value }))} className="w-full h-10 rounded-md border border-black/10 px-3 text-sm bg-white" data-testid="connect-host-instructor">
                <option value="">— Yo seré el anfitrión —</option>
                {instructors.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#6B7688]">Fecha y hora (hora Ciudad de México — avisa por push al llegar)</label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} data-testid="connect-scheduled" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#6B7688]">Programas destino (opcional, separa por comas — vacío = todos)</label>
              <Input placeholder="Ej: Formación 1, Formación 2" value={form.programas} onChange={(e) => setForm((f) => ({ ...f, programas: e.target.value }))} data-testid="connect-programas" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#6B7688]">Vincular a clase del Campus (opcional)</label>
              <select value={form.materia_id} onChange={(e) => setForm((f) => ({ ...f, materia_id: e.target.value }))} className="w-full h-10 rounded-md border border-black/10 px-3 text-sm bg-white" data-testid="connect-link-materia">
                <option value="">— Sin vincular —</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.titulo}{c.grupo ? ` · ${c.grupo}` : ""}{c.programa ? ` (${c.programa})` : ""}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#C0140F] hover:bg-[#a01109]" onClick={create} disabled={saving} data-testid="connect-create-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {linkFor && <LinkDialog session={linkFor} classes={classes} onClose={() => setLinkFor(null)} onDone={() => { setLinkFor(null); load(); }} />}

      {player && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { if (player.url) URL.revokeObjectURL(player.url); setPlayer(null); }} data-testid="connect-rec-player">
          <div className="bg-[#0A1420] rounded-2xl overflow-hidden w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10"><p className="font-extrabold text-white text-sm truncate">{player.titulo}</p><button onClick={() => { if (player.url) URL.revokeObjectURL(player.url); setPlayer(null); }} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button></div>
            <div className="bg-black flex items-center justify-center min-h-[240px]">
              {!player.url ? <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)] my-16" /> : <video src={player.url} controls autoPlay className="w-full max-h-[70vh]" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LinkDialog({ session, classes, onClose, onDone }) {
  const [sel, setSel] = useState(classes.find((c) => c.connect_session_id === session.id)?.id || "");
  const save = async () => {
    try { await connectApi.linkClass(session.id, sel); toast.success(sel ? "Clase vinculada" : "Vínculo quitado"); onDone(); }
    catch { toast.error("Error"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="connect-link-dialog">
        <DialogHeader><DialogTitle>Vincular «{session.titulo}» a una clase</DialogTitle><DialogDescription>El alumno podrá entrar en vivo directo desde su materia en el Campus.</DialogDescription></DialogHeader>
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="w-full h-10 rounded-md border border-black/10 px-3 text-sm bg-white" data-testid="connect-link-select">
          <option value="">— Sin vincular —</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.titulo}{c.grupo ? ` · ${c.grupo}` : ""}{c.programa ? ` (${c.programa})` : ""}</option>)}
        </select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button className="bg-[#0A1C30]" onClick={save} data-testid="connect-link-save">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordingsPanel({ recordings, onChanged, onPlay }) {
  if (recordings.length === 0) return <div className="text-center py-16 text-[#6B7688]" data-testid="connect-recs-empty"><Film className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-bold">Aún no hay grabaciones.</p><p className="text-sm">Al terminar una sesión grabada, aparecerá aquí y en Supera Drive.</p></div>;
  return (
    <div className="space-y-3" data-testid="connect-recs-list">
      {recordings.map((r) => <RecordingRow key={r.id} rec={r} onChanged={onChanged} onPlay={onPlay} />)}
    </div>
  );
}

function RecordingRow({ rec, onChanged, onPlay }) {
  const [titulo, setTitulo] = useState(rec.titulo || "");
  const [programas, setProgramas] = useState((rec.programas || []).join(", "));
  const [grupos, setGrupos] = useState((rec.grupos || []).join(", "));
  const [pub, setPub] = useState(!!rec.publicado);
  const [saving, setSaving] = useState(false);

  const save = async (publicar) => {
    setSaving(true);
    try {
      const payload = {
        titulo,
        programas: programas.split(",").map((x) => x.trim()).filter(Boolean),
        grupos: grupos.split(",").map((x) => x.trim()).filter(Boolean),
      };
      if (publicar !== undefined) payload.publicado = publicar;
      await connectApi.updateRecording(rec.id, payload);
      if (publicar !== undefined) setPub(publicar);
      toast.success(publicar === true ? "Publicada en el Campus" : publicar === false ? "Quitada del Campus" : "Guardado");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const del = async () => { if (!window.confirm("¿Eliminar esta grabación (también de Drive y Campus)?")) return; try { await connectApi.deleteRecording(rec.id); toast.success("Eliminada"); onChanged(); } catch { toast.error("Error"); } };

  return (
    <div className="rounded-2xl bg-white border border-black/5 p-4" data-testid={`connect-rec-${rec.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#C0140F]/10 grid place-items-center flex-shrink-0"><Film className="w-5 h-5 text-[#C0140F]" /></div>
        <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="flex-1" data-testid={`rec-titulo-${rec.id}`} />
        {pub && <span className="text-[10px] font-extrabold px-2 py-1 rounded-md bg-[#E2F6EB] text-[#22A860]">EN CAMPUS</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        <div><label className="text-[11px] font-bold text-[#6B7688]">Programas (coma)</label><Input value={programas} onChange={(e) => setProgramas(e.target.value)} placeholder="Todos si vacío" data-testid={`rec-programas-${rec.id}`} /></div>
        <div><label className="text-[11px] font-bold text-[#6B7688]">Grupos (coma)</label><Input value={grupos} onChange={(e) => setGrupos(e.target.value)} placeholder="Todos del programa si vacío" data-testid={`rec-grupos-${rec.id}`} /></div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => onPlay(rec)}><Eye className="w-4 h-4 mr-1" /> Ver</Button>
        <Button size="sm" variant="outline" onClick={() => save(undefined)} disabled={saving} data-testid={`rec-save-${rec.id}`}><Save className="w-4 h-4 mr-1" /> Guardar</Button>
        {pub
          ? <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} data-testid={`rec-unpublish-${rec.id}`}>Quitar del Campus</Button>
          : <Button size="sm" className="bg-[#22A860] hover:bg-[#1c8f50]" onClick={() => save(true)} disabled={saving} data-testid={`rec-publish-${rec.id}`}>Publicar en Campus</Button>}
        <Button size="sm" variant="outline" onClick={del} data-testid={`rec-del-${rec.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>
    </div>
  );
}
